import { useMemo } from "react";
import AppConstants from "../constants/AppConstants";
import ActiveBlockContainerStore from "../stores/ActiveBlockContainerStore.mjs";
import SiteDesignStore from "../stores/SiteDesignStore";
import useStoreState from "../components/providers/useStoreState";
import { usePage } from "./usePage";

const { VALUE_THIS, MAX_BLOCK_STATE_VIEWS } = AppConstants;

const EMPTY_RESULT = { maxViews: MAX_BLOCK_STATE_VIEWS, hasNoViews: false, states: null };

const getStatesResult = (states) => {
  if (!states) return { maxViews: 0, hasNoViews: true, states: null };
  const viewCount = Math.min(states.size, MAX_BLOCK_STATE_VIEWS);
  return { maxViews: viewCount, hasNoViews: states.size === 0, states };
};

const findBlockInPageData = (fetchedPageData, blockSlug) => {
  return fetchedPageData.get("blockData")?.find((b) => b?.get("slug") === blockSlug);
};

const findBlockInActiveContainer = (activeContainer, blockSlug) => {
  if (blockSlug === VALUE_THIS) {
    return activeContainer.get("selectedBlock");
  }
  return activeContainer.get("blocks")?.find((b) => b?.get("slug") === blockSlug);
};

const getStatesFromSiteBlockEntity = (pBlockId) => {
  const siteBlockEntity = SiteDesignStore.getDesign().getBlockEntity(pBlockId);
  return siteBlockEntity ? getStatesResult(siteBlockEntity.data.get("states")) : EMPTY_RESULT;
};

const getStatesFromCrossPageBlock = (block) => {
  const pBlockId = block.get("pBlockId");
  if (pBlockId) {
    return getStatesFromSiteBlockEntity(pBlockId);
  }
  return getStatesResult(block.get("states"));
};

const getStatesFromLocalBlock = (block) => {
  const entityId = block.get("pBlockId") || block.get("id");
  const blockEntity = SiteDesignStore.getBlockContainer().getBlockEntity(entityId);

  if (blockEntity) {
    return getStatesResult(blockEntity.data.get("states"));
  }

  if (block.has("pBlockId")) {
    return getStatesFromSiteBlockEntity(block.get("pBlockId"));
  }

  return EMPTY_RESULT;
};

/**
 * Gets block states for a block slug.
 * @param {string} blockSlug - block slug or VALUE_THIS for selected block
 * @param {string} [pageSlug] - optional page slug to fetch blocks from a different page
 */
export const useBlockStates = (blockSlug, pageSlug = null) => {
  const activeContainer = useStoreState(
    ActiveBlockContainerStore,
    ActiveBlockContainerStore.getContainer,
  );

  const currentPageSlug = activeContainer?.get("slug");
  const needsCrossPageFetch = pageSlug && pageSlug !== currentPageSlug;

  const { pageData: fetchedPageData } = usePage(pageSlug, {
    skip: !needsCrossPageFetch || !blockSlug,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  return useMemo(() => {
    if (!blockSlug || !activeContainer) return EMPTY_RESULT;

    if (needsCrossPageFetch) {
      if (!fetchedPageData) return EMPTY_RESULT;
      const block = findBlockInPageData(fetchedPageData, blockSlug);
      return block ? getStatesFromCrossPageBlock(block) : EMPTY_RESULT;
    }

    const block = findBlockInActiveContainer(activeContainer, blockSlug);
    if (!block || block.isEmpty()) return EMPTY_RESULT;

    return getStatesFromLocalBlock(block);
  }, [blockSlug, pageSlug, activeContainer, fetchedPageData]);
};
